import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { highlightText } from '../utils/searchUtils';

/**
 * NavigationCard Component
 * 
 * A reusable card component for displaying navigation options on the home page.
 * Simple, clean design with hover effects and accessibility features.
 * 
 * @param {Object} props
 * @param {ReactElement} props.icon - Material-UI icon component
 * @param {string} props.title - Display name for the card
 * @param {string} props.description - Optional description text
 * @param {string} props.route - Navigation path (optional for groups)
 * @param {Array} props.subItems - Array of child navigation items (optional)
 * @param {string} props.color - Accent color for the card (hex or named color)
 * @param {function} props.onClick - Click handler for navigation or selection
 * @param {boolean} props.isSelected - Whether the card is currently selected
 * @param {Object} props.themeStyles - Theme-specific styling object (optional)
 * @param {string} props.searchQuery - Search query for highlighting (optional)
 * @param {boolean} props.isFavorite - Whether the card is marked as favorite
 * @param {boolean} props.isPinned - Whether the card is pinned
 * @param {function} props.onToggleFavorite - Handler for favorite toggle
 * @param {function} props.onTogglePin - Handler for pin toggle
 * @param {boolean} props.animationsEnabled - Whether animations are enabled
 */
const NavigationCard = ({
  icon,
  title,
  description,
  route,
  subItems = [],
  color = '#2196F3',
  onClick,
  isSelected = false,
  themeStyles = {},
  viewMode = 'grid',
  cardSize = 2,
  searchQuery = '',
  isFavorite = false,
  isPinned = false,
  onToggleFavorite,
  onTogglePin,
  animationsEnabled = true,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const hasSubItems = subItems && subItems.length > 0;

  // Trigger entrance animation on mount
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  // Determine if this is list view
  const isListView = viewMode === 'list';
  const isCompactView = viewMode === 'compact';

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  // Calculate dynamic sizing based on cardSize and viewMode
  // Responsive sizing with breakpoints
  const iconSize = isCompactView ? 32 : 40 + (cardSize * 5);
  const iconFontSize = isCompactView ? 18 : 22 + (cardSize * 2);
  const titleFontSize = isCompactView ? '13px' : `${15 + cardSize}px`;
  const padding = isCompactView ? 1 : 1.5;
  
  // Mobile-specific adjustments
  const mobileIconSize = isCompactView ? 28 : Math.max(iconSize * 0.85, 32);
  const mobileTitleFontSize = isCompactView ? '12px' : `${Math.max(parseInt(titleFontSize) * 0.9, 13)}px`;

  // Highlight text segments if search query exists
  const titleSegments = highlightText(title, searchQuery);

  // Handle favorite toggle with event propagation stop
  const handleFavoriteClick = (event) => {
    event.stopPropagation();
    if (onToggleFavorite) {
      onToggleFavorite();
    }
  };

  // Handle pin toggle with event propagation stop
  const handlePinClick = (event) => {
    event.stopPropagation();
    if (onTogglePin) {
      onTogglePin();
    }
  };

  // Define animation keyframes
  const entranceAnimation = animationsEnabled ? {
    '@keyframes fadeInUp': {
      from: {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
  } : {};

  return (
    <Card
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${hasSubItems ? 'Select' : 'Navigate to'} ${title}`}
      sx={{
        // Base styles
        height: isListView ? 'auto' : '100%',
        // Maintain aspect ratio for grid/compact views
        aspectRatio: isListView ? 'auto' : { xs: '1 / 1', sm: '1 / 1' },
        cursor: 'pointer',
        transition: animationsEnabled ? 'all 0.3s ease-in-out' : 'none',
        elevation: isHovered || isSelected ? 8 : 2,
        transform: animationsEnabled && isHovered && !isListView ? 'translateY(-4px)' : 'translateY(0)',
        borderLeft: `${isSelected ? '6px' : '4px'} solid ${color}`,
        display: 'flex',
        flexDirection: 'column',
        // Add subtle border for pinned cards
        ...(isPinned && {
          borderTop: `2px solid ${color}`,
          borderTopLeftRadius: '4px',
        }),
        // Entrance animation
        ...(animationsEnabled && {
          animation: isVisible ? 'fadeInUp 0.4s ease-out' : 'none',
          opacity: isVisible ? 1 : 0,
          ...entranceAnimation,
        }),
        // Theme styles (applied first, can be overridden by interaction styles)
        ...themeStyles.card,
        // Interaction styles (override theme styles for hover/focus)
        boxShadow: (isHovered || isSelected)
          ? (themeStyles.card?.['&:hover']?.boxShadow || '0 8px 16px rgba(0,0,0,0.2)')
          : (themeStyles.card?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'),
        opacity: isSelected ? 0.95 : (animationsEnabled && !isVisible ? 0 : 1),
        '&:hover': {
          borderLeftWidth: '6px',
          transform: animationsEnabled 
            ? (isListView ? 'translateX(4px)' : 'translateY(-4px)')
            : 'none',
          ...(themeStyles.card?.['&:hover'] || {}),
        },
        '&:focus': {
          outline: `2px solid ${color}`,
          outlineOffset: '2px',
        },
      }}
    >
      <CardContent sx={{ 
        flexGrow: 1, 
        p: { xs: padding * 0.75, sm: padding }, 
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}>
        {/* Pin Icon - Top Left */}
        {onTogglePin && (
          <Tooltip title={isPinned ? "Unpin card" : "Pin card"} placement="top">
            <IconButton
              onClick={handlePinClick}
              aria-label={isPinned ? "unpin card" : "pin card"}
              sx={{
                position: 'absolute',
                top: { xs: 2, sm: 4 },
                left: { xs: 2, sm: 4 },
                padding: { xs: '3px', sm: '4px' },
                opacity: isPinned || isHovered ? 1 : 0,
                transition: 'all 0.2s ease',
                color: isHovered ? 'white' : (isPinned ? color : 'text.secondary'),
                // Mobile: Always show on touch devices
                '@media (hover: none)': {
                  opacity: 1,
                },
                '&:hover': {
                  color: 'white',
                  bgcolor: `${color}40`,
                },
              }}
            >
              {isPinned ? (
                <PushPinIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              ) : (
                <PushPinOutlinedIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Favorite Icon - Top Right */}
        {onToggleFavorite && (
          <Tooltip title={isFavorite ? "Remove from favorites" : "Add to favorites"} placement="top">
            <IconButton
              onClick={handleFavoriteClick}
              aria-label={isFavorite ? "remove from favorites" : "add to favorites"}
              sx={{
                position: 'absolute',
                top: { xs: 2, sm: 4 },
                right: { xs: 2, sm: 4 },
                padding: { xs: '3px', sm: '4px' },
                opacity: isFavorite || isHovered ? 1 : 0,
                transition: 'all 0.2s ease',
                color: isHovered ? 'white' : (isFavorite ? '#FFD700' : 'text.secondary'),
                // Mobile: Always show on touch devices
                '@media (hover: none)': {
                  opacity: 1,
                },
                '&:hover': {
                  color: 'white',
                  bgcolor: 'rgba(255, 215, 0, 0.3)',
                },
              }}
            >
              {isFavorite ? (
                <StarIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
              )}
            </IconButton>
          </Tooltip>
        )}

        {/* Icon and Title Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: isListView ? { xs: 1.5, sm: 2 } : { xs: 1, sm: 1.5 },
            flexDirection: isListView ? 'row' : 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Responsive icon container size
              width: { xs: mobileIconSize, sm: iconSize },
              height: { xs: mobileIconSize, sm: iconSize },
              borderRadius: '50%',
              bgcolor: `${color}20`,
              color: color,
              transition: animationsEnabled ? 'all 0.3s ease-in-out' : 'none',
              transform: animationsEnabled && isHovered ? 'scale(1.1) rotate(5deg)' : 'scale(1)',
              flexShrink: 0,
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                fontSize: { 
                  xs: iconFontSize * 0.85, 
                  sm: iconFontSize 
                },
                transition: animationsEnabled ? 'transform 0.3s ease-in-out' : 'none',
              } 
            })}
          </Box>
          <Box sx={{ flex: 1, textAlign: isListView ? 'left' : 'center' }}>
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{
                fontWeight: 600,
                fontFamily: '"brandon-grotesque-n7", "brandon-grotesque", sans-serif',
                // Responsive font size
                fontSize: { xs: mobileTitleFontSize, sm: titleFontSize },
                lineHeight: 1.3,
                // Apply theme cardTitle styles
                ...themeStyles.cardTitle,
              }}
            >
              {titleSegments.map((segment, index) => (
                segment.isMatch ? (
                  <Box
                    key={index}
                    component="span"
                    sx={{
                      backgroundColor: '#ffeb3b',
                      color: '#000',
                      padding: { xs: '1px 3px', sm: '2px 4px' },
                      borderRadius: '2px',
                      fontWeight: 700,
                      ...(animationsEnabled && {
                        animation: 'highlightPulse 0.6s ease',
                        '@keyframes highlightPulse': {
                          '0%': { 
                            backgroundColor: '#ffeb3b',
                            transform: 'scale(1)',
                          },
                          '50%': { 
                            backgroundColor: '#ffd700',
                            transform: 'scale(1.05)',
                          },
                          '100%': { 
                            backgroundColor: '#ffeb3b',
                            transform: 'scale(1)',
                          },
                        },
                      }),
                    }}
                  >
                    {segment.text}
                  </Box>
                ) : (
                  <span key={index}>{segment.text}</span>
                )
              ))}
            </Typography>
            {hasSubItems && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: isCompactView ? { xs: '9px', sm: '10px' } : { xs: '10px', sm: '11px' },
                  opacity: 0.65,
                  ...themeStyles.cardDescription,
                }}
              >
                {subItems.length} options
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default NavigationCard;
